import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgIf} from "@angular/common";
import {changeCompany, clearAssistantPrompts, clearChangeCompanyState} from "../../../state/report/report.actions";
import {filter, Subscription, take} from "rxjs";
import {selectChangeCompanyFailure, selectChangeCompanySuccess} from "../../../state/report/report.selectors";
import {updateReport} from "../../../state/search/search.actions";
import {selectReport} from "../../../state/search/search.selectors";
import {Store} from "@ngrx/store";
import {ReportState} from "../../../state/report/report.reducer";
import {PopupService} from "../../../services/popup.service";
import {SharedService} from "../../../services/shared.service";
import {sameCompanyName} from "src/app/utils/normalize-company.util";

@Component({
  selector: 'app-change-company-modal',
  standalone: true,
  imports: [
    NgIf
  ],
  templateUrl: './change-company-modal.component.html',
  styleUrl: './change-company-modal.component.scss'
})
export class ChangeCompanyModalComponent implements OnInit, OnDestroy {

  @Input() reportSearch: any;

  companyFound = false;
  displayMessage = '';

  selectedCompany: any = null;
  isVisible: boolean = false;

  // Prevents a double-click / re-entry from dispatching the credit-consuming
  // switch-company call twice.
  isChanging = false;

  private changeCompanySub?: Subscription;
  private successSub?: Subscription;
  private failureSub?: Subscription;
  private persistSub?: Subscription;

  constructor(
    private store: Store<{ report: ReportState }>,
    private popupService: PopupService,
    private sharedService: SharedService
  ) {}

  ngOnInit() {
    this.listenForChangeCompany()
  }

  ngOnDestroy() {
    this.changeCompanySub?.unsubscribe();
    this.successSub?.unsubscribe();
    this.failureSub?.unsubscribe();
    this.persistSub?.unsubscribe();
  }

  listenForChangeCompany() {
    // Fired when a company is clicked in the change-company dropdown.
    this.changeCompanySub = this.sharedService.changeCompany$.subscribe((company) => {
      this.selectedCompany = company;
      this.isChanging = false;
      this.checkIfCompanyExists();
      this.handleIsVisible(true);
    });
  }

  /**
   * Determines whether the picked company has already been scanned for this
   * report. An already-scanned company can be switched to for free, so the
   * modal copy reflects that no credit will be consumed.
   */
  private checkIfCompanyExists(): void {
    if (this.selectedCompany && this.reportSearch?.search_companies?.length) {
      this.companyFound = this.companyAlreadyScanned(this.selectedCompany);

      if (this.companyFound) {
        this.displayMessage = `${this.selectedCompany.name} has already been scanned. Switching will not consume a credit.`;
      } else {
        this.displayMessage = `The company ${this.selectedCompany.name} was not found. <br /> This will consume one credit.`;
      }
    } else {
      // No scanned companies yet → this is always a fresh (paid) scan.
      this.companyFound = false;
      this.displayMessage = `The company ${this.selectedCompany?.name} was not found. <br /> This will consume one credit.`;
    }
  }

  /** Finds the already-scanned search_companies entry matching `company`, or undefined. */
  private findScannedCompany(company: any): any {
    const orgId = company?.organization_id;
    const name = company?.name;
    return (this.reportSearch?.search_companies || []).find(
      (c: any) =>
        // Prefer the reliable Apollo organization id; fall back to name.
        (!!orgId && c.apollo_organisation_id === orgId) ||
        (!!name && sameCompanyName(c.name, name))
    );
  }

  /** True when `company` is already scanned for this report (so switching is free). */
  private companyAlreadyScanned(company: any): boolean {
    return !!this.findScannedCompany(company);
  }

  handleIsVisible(isVisible: boolean): void {
    this.isVisible = isVisible;
  }

  confirmSelection(): void {
    if (this.isChanging) {
      return;
    }
    this.handleChangeCompany();
  }

  handleChangeCompany(): void {
    if (!this.selectedCompany) {
      this.popupService.notify('No company selected or invalid details.', 'Error', 'error');
      return;
    }

    // The switch-company scan identifies the company by its Apollo organization_id
    // (sourced from employment_history). Without it the backend can't resolve the
    // company, so abort rather than fire an unscannable request.
    const searchId = this.selectedCompany.search_id;
    const organizationId = this.selectedCompany.organization_id;

    if (!organizationId) {
      this.popupService.notify('Unable to switch — missing company id.', 'Error', 'error');
      return;
    }

    // Recompute freshly at confirm time — never trust the cached `companyFound`.
    const alreadyScanned = this.companyAlreadyScanned(this.selectedCompany);

    if (alreadyScanned) {
      // Already scanned → switch INSTANTLY from local state (its data is already
      // loaded, so no "Gathering data" flash), and persist the selection on the
      // backend in the BACKGROUND so it survives a refresh. The backend re-points
      // search_company_id by organization_id with no scan and no credit.
      this.handleIsVisible(false);
      this.switchToExistingCompany();
      this.persistActiveCompany(searchId, organizationId);
      return;
    }

    // New company → credit-consuming scan via the switch-company endpoint.
    this.isChanging = true;
    this.handleIsVisible(false);
    this.sharedService.emitChangeCompanyBanner('Please wait, we are changing the company...', true, true);

    // Clear the previous company's assistant prompts while the new scan runs.
    this.store.dispatch(clearAssistantPrompts());

    // Dispatch the action to change the company (sets result back to null).
    this.store.dispatch(changeCompany({ searchId, organizationId }));

    // Tear down any previous in-flight listeners before re-subscribing.
    this.successSub?.unsubscribe();
    this.failureSub?.unsubscribe();

    this.successSub = this.store.select(selectChangeCompanySuccess)
      .pipe(
        filter((result) => result !== null),
        take(1)
      )
      .subscribe({
        next: (result) => {
          const company = result?.search || result;
          if (company) {
            this.applyNewCompanyToReport(company);
            this.sharedService.emitChangeCompanyBanner('Company changed successfully.', false, true);
          } else {
            console.warn('Unexpected response structure in result:', result);
          }

          this.isChanging = false;
          this.store.dispatch(clearChangeCompanyState());
        },
        error: (error) => {
          this.isChanging = false;
          console.error('Error in success subscription:', error);
        },
      });

    this.failureSub = this.store.select(selectChangeCompanyFailure)
      .pipe(
        filter((error) => error !== null),
        take(1)
      )
      .subscribe({
        next: (error) => {
          const errorMessage = error.error?.message || 'Failed to change company.';
          this.sharedService.emitChangeCompanyBanner(errorMessage, false, true);

          this.isChanging = false;
          this.store.dispatch(clearChangeCompanyState());
        },
        error: (error) => {
          this.isChanging = false;
          console.error('Error in failure subscription:', error);
        },
      });
  }

  /**
   * Switches the active company to one that has already been scanned — purely by
   * re-pointing the report at the existing search_companies entry, so it shows
   * instantly with no scan and no credit. The report component re-derives the
   * displayed company (restoring its insights from the store / its in-session
   * cache). Backend persistence is handled separately by persistActiveCompany().
   */
  private switchToExistingCompany(): void {
    this.store.select(selectReport).pipe(take(1)).subscribe((response: any) => {
      const currentReport = response?.report;
      if (!currentReport?.search_companies) {
        return;
      }

      const orgId = this.selectedCompany?.organization_id;
      const existing = currentReport.search_companies.find(
        (c: any) =>
          (!!orgId && c.apollo_organisation_id === orgId) ||
          sameCompanyName(c.name, this.selectedCompany.name)
      );
      if (!existing) {
        return;
      }

      const updatedReport = {
        ...currentReport,
        search_company_id: existing.id,
        insights: existing.insights || {},
      };

      this.store.dispatch(updateReport({ report: updatedReport }));
      this.sharedService.emitChangeCompanyBanner('Company changed.', false, true);
    });
  }

  /**
   * Persists the active company on the backend WITHOUT disturbing the UI — used
   * after an already-scanned local switch so the selection survives a refresh.
   * The switch-company endpoint re-points search_company_id by organization_id
   * (no scan, no credit); the response is ignored (the UI is already updated).
   */
  private persistActiveCompany(searchId: number, organizationId: string): void {
    this.persistSub?.unsubscribe();
    this.store.dispatch(changeCompany({ searchId, organizationId }));
    this.persistSub = this.store
      .select(selectChangeCompanySuccess)
      .pipe(filter((result) => result !== null), take(1))
      .subscribe(() => this.store.dispatch(clearChangeCompanyState()));
  }

  /**
   * After a fresh scan, merge the newly scanned company into the report's
   * search_companies array and point search_company_id at it, so every sibling
   * (assistant, edit-revenue, AI tool box, …) follows the new active company.
   */
  private applyNewCompanyToReport(company: any): void {
    this.store.select(selectReport).pipe(take(1)).subscribe((response: any) => {
      const currentReport = response?.report;
      if (!currentReport) {
        return;
      }

      const list = currentReport.search_companies || [];
      const idx = list.findIndex(
        (c: any) => c.id === company.id || sameCompanyName(c.name, company.name)
      );

      const search_companies = idx >= 0
        ? list.map((c: any, i: number) => (i === idx ? company : c))
        : [...list, company];

      const updatedReport = {
        ...currentReport,
        search_companies,
        search_company_id: company.id,
        insights: company.insights || {},
      };

      this.store.dispatch(updateReport({ report: updatedReport }));
    });
  }

}
